"use client";

import Image from "next/image";
import Link from "next/link";
import React, { useState, useEffect, useRef, useCallback } from "react";
import parse from "html-react-parser";
import { useGSAP } from "@gsap/react";
import Title from "@/core/common/Title";

const removeEmptyParagraphs = (htmlString: string) => {
  return htmlString.replace(/<p>\s*<\/p>/g, "");
};

const animateSplitText = (elements: HTMLDivElement[], delay: number) => {
  elements.forEach((el) => {
    const text = el.textContent || "";
    el.innerHTML = text
      .split("")
      .map(
        (char) =>
          `<span class="inline-block opacity-0 transform translate-y-4">${char}</span>`
      )
      .join("");
    const spans = el.querySelectorAll("span");
    spans.forEach((span, index) => {
      setTimeout(
        () => {
          span.classList.add("opacity-100", "translate-y-0");
          span.classList.remove("translate-y-4");
        },
        delay * index * 1000
      );
    });
  });
};

interface HolidayByMonthProps {
  packagesByMonth: any[];
  month: string;
}

const HolidayByMonth = ({ packagesByMonth }: HolidayByMonthProps) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const titleRefs = useRef<HTMLDivElement[]>([]);

  useGSAP(() => {
    if (!titleRefs.current) return;
    animateSplitText(titleRefs.current, 0.2);
  }, []);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const startAutoSlide = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setActiveIndex((prev) =>
        prev === packagesByMonth.length - 1 ? 0 : prev + 1
      );
    }, 6000);
  }, [packagesByMonth.length]);

  useEffect(() => {
    startAutoSlide();
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [packagesByMonth, startAutoSlide]);

  const handleClick = (index: number) => {
    setActiveIndex(index);
    startAutoSlide();
  };

  if (!packagesByMonth || !packagesByMonth.length) return null;

  return (
    <div className="relative w-full flex z-10 bg-white flex-col justify-center padding-x">
      <Title greenText="Holidays " yellowText="By Month" />
      <div className="mt-10 flex flex-row gap-1 lg:gap-4 overflow-x-auto scrollbar-hide">
        {packagesByMonth.map((pkg, index) => {
          return (
            <div
              key={pkg.id}
              className="shrink-0 flex items-start gap-4 lg:gap-8 transition-all duration-500 ease-in-out h-[70vw] sm:h-[45.57vw] lg:h-[30.53vw]"
            >
              <div
                onClick={() => handleClick(index)}
                className={`relative overflow-hidden cursor-pointer transition-all duration-700 ease-in-out ${
                  activeIndex === index
                    ? "w-[25vw] lg:w-[40vw]"
                    : "w-[10vw] lg:w-[10vw]"
                } h-[70vw] sm:h-[45.57vw] lg:h-[30.53vw]`}
              >
                <Image
                  src={pkg?.image || "/placeholder.png"}
                  alt={pkg?.name}
                  fill
                  sizes="(max-width: 768px) 100vw, 50vw"
                  className={`object-cover transition-all duration-700 ease-in-out ${
                    activeIndex === index ? "w-[40vw]" : "w-[10vw] lg:w-[40vw]"
                  } h-[70vw] sm:h-[45.57vw] lg:h-[30.53vw]`}
                />
                {activeIndex !== index && (
                  <p className="text-white absolute bottom-[5%] lg:bottom-[20%] left-[10%] lg:left-[-10%] w-full transform rotate-270 transition-transform duration-500 whitespace-nowrap font_45 font-medium font-secondary-font">
                    {(pkg?.month || "")
                      .trim()
                      .replace(/^\w/, (c: string) => c.toUpperCase())}
                  </p>
                )}
              </div>

              {activeIndex === index && (
                <div className="flex flex-col justify-between h-full w-[25vw] lg:w-[25vw] transition-opacity duration-500 ease-in-out">
                  <div className="text-[5vw] sm:text-[2.34vw] lg:text-[1.67vw] font-medium">
                    <span className="mr-2 block sm:inline">
                      {(pkg?.month || "")
                        .trim()
                        .replace(/^\w/, (c: string) => c.toUpperCase())}
                    </span>
                  </div>

                  <div className="flex flex-col gap-6 mt-4">
                    <div className="text-[3.2vw] sm:text-[1.37vw] lg:text-[0.85vw] leading-relaxed text-[#888] line-clamp-6">
                      {parse(removeEmptyParagraphs(pkg?.description || ""))}
                    </div>
                    <Link href={`package/${pkg?.slug}`} scroll={false}>
                      <button className="bg-[#64A058] text-white typography-small-body rounded-sm md:typography-mid-body-medium px-2 py-1 md:px-6 md:py-2 md:rounded-lg w-max">
                        Explore Now
                      </button>
                    </Link>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HolidayByMonth;
